import type { ReactNode } from "react";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";

interface SectionHeaderProps {
  title: string;
  /** Subtle description below the title */
  subtitle?: string;
  /** Optional right-side content (action link, export button, etc.) */
  action?: ReactNode;
  /** Count badge shown next to title */
  count?: number;
  /** Make the section collapsible */
  collapsible?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}

export const SectionHeader = ({
  title,
  subtitle,
  action,
  count,
  collapsible = false,
  isExpanded = true,
  onToggle,
  children,
}: SectionHeaderProps) => {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: isExpanded ? 1 : 0, cursor: collapsible ? "pointer" : "default" }}
        onClick={collapsible ? onToggle : undefined}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {collapsible && (
            <IconButton size="small" sx={{ p: 0, mr: 0.25 }}>
              {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
          )}
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.secondary", lineHeight: 1.5 }}
          >
            {title}
          </Typography>
          {count !== undefined && (
            <Typography variant="caption" color="text.disabled" fontWeight={500}>
              {count}
            </Typography>
          )}
        </Stack>
        {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
      </Stack>
      {subtitle && isExpanded && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block", mt: -0.25 }}>
          {subtitle}
        </Typography>
      )}
      {collapsible ? (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      ) : (
        children
      )}
    </Box>
  );
};
